import { Fragment, useMemo, useState } from 'react';
import { BarRounded } from '@visx/shape';
import { Group } from '@visx/group';
import { scaleBand } from '@visx/scale';
import { useTooltip } from '@visx/tooltip';
import { Axis, AxisLeft } from '@visx/axis';
import { GridColumns } from '@visx/grid';
import { localPoint } from '@visx/event';
import { ScaleLinear, ScaleOrdinal } from 'd3-scale';
import { truncate } from 'lodash';
import { common, model } from '@gosupersimple/types';

import { formatPropertyValue } from '@/explore/utils/format';
import { GroupFilter } from '@/explore/utils/drilldown';
import { InteractableProps } from '@/explore/utils/interactable';

import { ChartTooltipSingleValue, renderGridColumns } from '../common';
import { CategoryData } from '../grouped-chart/types';
import { getMaxLabelWidth, getScreenPoint, increaseBrightness } from '../utils';
import {
  explodeCombinedKey,
  getLeftRightAxisFormats,
  getLeftRightAxisTypes,
  isCombinedKey,
} from '../grouped-chart/utils';
import { NumXTicks } from './grouped-horizontal-chart';
import { convertTicksToScale } from '../grouped-chart/utils/scale';

import { CategoryValue } from '../category-chart/types';

import commonStyles, { gridLineColor } from '../charts.module.scss';
import styles from './horizontal-bar-chart.module.scss';

type TooltipData = {
  categoryLabel: string;
  categoryValue: CategoryValue;
  valueLabel: string;
  valueKey: string;
  value: number | null;
  color: string;
  type: model.PropertyType | null;
  format?: common.PropertyValueFormat;
};

interface HorizontalBarChartProps {
  categoryFormat: {
    precision?: string;
  };
  onCategoryClick?: (params: {
    x: number;
    y: number;
    key: string;
    groupFilters: GroupFilter[];
  }) => void;
  data: CategoryData;
  width: number;
  valueScales: {
    left: ScaleLinear<number, number>;
    right: ScaleLinear<number, number>;
  };
  colorScale: ScaleOrdinal<string, string>;
  height?: number;
}

const MaxHeight = 600;
const GroupWidth = parseInt(commonStyles.widthPerGroup);
const CategoryPadding = 0.3;
const OuterPadding = 0.4;
const InnerPadding = 0.2;
const BarMinHeight = 3;
const BarMaxHeight = 30;
const CategoryLabelMaxLength = 26;

export const HorizontalBarChart = (props: HorizontalBarChartProps) => {
  const { data, width, height: chartHeight, valueScales, colorScale, onCategoryClick } = props;
  const { showTooltip, hideTooltip, tooltipOpen, tooltipLeft, tooltipTop, tooltipData } =
    useTooltip<TooltipData>();
  const [hoveredCategoryValue, setHoveredCategoryValue] = useState<string | null>(null);

  const handleMouseOver = (event: any, data: TooltipData) => {
    const point = localPoint(event);
    if (point !== null) {
      const screenPoint = getScreenPoint(point, event.target);
      showTooltip({ tooltipLeft: screenPoint.x, tooltipTop: screenPoint.y, tooltipData: data });
    }
  };

  const axisTypes = getLeftRightAxisTypes(data.series);
  const axisFormats = getLeftRightAxisFormats(data.series);

  const formatBottomAxisValue = (n: number) => {
    const value = axisTypes.left === 'Integer' ? Math.round(n) : Math.round(n * 100) / 100;
    return formatPropertyValue(value, {
      type: axisTypes.left,
      format: axisFormats.left,
      style: 'compact',
    });
  };

  const formatTopAxisValue = (n: number) => {
    const value = axisTypes.right === 'Integer' ? Math.round(n) : Math.round(n * 100) / 100;
    return formatPropertyValue(value, {
      type: axisTypes.right,
      format: axisFormats.right,
      style: 'compact',
    });
  };

  const barsCount = data.items.length * data.series.length;
  const height = chartHeight ?? Math.min(barsCount * BarMaxHeight, MaxHeight);

  const categoryScale = scaleBand({
    domain: data.items.map((d) => d.categoryValue.formatted),
    paddingOuter: OuterPadding,
    padding: CategoryPadding,
    range: [0, height],
  });

  const maxBarsInCategory = Math.max(...data.items.map((item) => Object.keys(item.values).length));

  const groupSeriesScale = scaleBand({
    domain: new Array(maxBarsInCategory).fill(null).map((_, i) => i),
    paddingInner: maxBarsInCategory > 1 ? InnerPadding : 0,
    range: [0, categoryScale.bandwidth()],
  });
  const keys = data.series.map(({ key }) => key);

  const domainSizes = {
    left: valueScales.left.domain()[1] - valueScales.left.domain()[0],
    right: valueScales.right.domain()[1] - valueScales.right.domain()[0],
  };

  const hasRightAxisSeries = data.series.some((series) => series.axis === 'right');

  // Default to top as main axis in case no series on bottom axis
  const primaryValueScale = domainSizes.left > 0 ? valueScales.left : valueScales.right;

  // Force top axis ticks to align with primary axis ticks
  const topAxisTicks = convertTicksToScale(
    primaryValueScale.ticks(NumXTicks),
    primaryValueScale,
    valueScales.right,
  );

  const groupLabels = useMemo(() => {
    return data.items.map((item) => item.categoryValue.formatted);
  }, [data.items]);

  const dynamicGroupWidth = useMemo(() => {
    const labels = groupLabels.map((label) => truncate(label, { length: CategoryLabelMaxLength }));
    const maxWidth = getMaxLabelWidth(labels);
    return Math.min(Math.ceil(maxWidth), GroupWidth);
  }, [groupLabels]);

  return (
    <div className={commonStyles.graph}>
      {tooltipData !== undefined ? (
        <ChartTooltipSingleValue
          position={{ x: tooltipLeft ?? 0, y: tooltipTop ?? 0 }}
          tooltipData={{
            label: tooltipData.categoryValue.formatted,
            value: tooltipData.value,
            type: tooltipData.type,
            format: tooltipData.format,
          }}
          tooltipOpen={tooltipOpen}
          categoryLabel={tooltipData.categoryLabel}
          valueLabel={tooltipData.valueLabel}
          seriesColor={colorScale(tooltipData.valueKey)}
        />
      ) : null}
      <svg width={width} height={height}>
        <Group left={dynamicGroupWidth}>
          <GridColumns
            width={width}
            height={height}
            scale={primaryValueScale}
            tickValues={primaryValueScale.ticks(NumXTicks)}
            stroke={gridLineColor}
            // eslint-disable-next-line react/no-children-prop
            children={(lines) => renderGridColumns(lines, { width, strokeColor: gridLineColor })}
          />
          {data.items.flatMap((item, idx) => {
            const keysWithValues = keys.filter((key) => item.values[key] !== undefined);

            return keysWithValues.map((key, barIdx) => {
              const value = item.values[key];
              if (value === null) {
                return null;
              }
              const series = data.series.find((s) => s.key === key);
              if (series === undefined) {
                throw new Error(`Series not found for bar chart key: ${key}`);
              }
              const shiftAmount = // Align vertically if not all bars in category have values
                ((maxBarsInCategory - keysWithValues.length) *
                  (groupSeriesScale.bandwidth() * (1 + InnerPadding))) /
                2;
              const y =
                (categoryScale(item.categoryValue.formatted) ?? 0) +
                (groupSeriesScale(barIdx) ?? 0) +
                shiftAmount;

              const seriesValueScale = valueScales[series.axis];

              return (
                <Fragment key={`${idx}-${key}`}>
                  <BarRounded
                    {...InteractableProps}
                    x={0}
                    y={y}
                    width={seriesValueScale(value)}
                    height={Math.max(BarMinHeight, groupSeriesScale.bandwidth())}
                    radius={4}
                    right
                    fill={
                      hoveredCategoryValue === item.categoryValue.formatted
                        ? increaseBrightness(colorScale(key), 10)
                        : colorScale(key)
                    }
                    onMouseMove={(e) => {
                      return handleMouseOver(e, {
                        value,
                        valueKey: key,
                        categoryLabel: item.label ?? '',
                        categoryValue: item.categoryValue,
                        valueLabel: series?.label ?? key,
                        color: colorScale(key),
                        type: series?.type ?? 'Number',
                        format: series?.format,
                      });
                    }}
                    onMouseOver={() => setHoveredCategoryValue(item.categoryValue.formatted)}
                    onMouseOut={() => {
                      hideTooltip();
                      setHoveredCategoryValue(null);
                    }}
                    onClick={(e) => {
                      onCategoryClick?.({
                        x: e.clientX,
                        y: e.clientY,
                        key: isCombinedKey(series.key)
                          ? explodeCombinedKey(series.key).valueKey
                          : series.key,
                        groupFilters: [
                          ...(isCombinedKey(series.key)
                            ? [
                                {
                                  key: explodeCombinedKey(series.key).groupKey,
                                  value: explodeCombinedKey(series.key).groupValue,
                                },
                              ]
                            : []),
                          {
                            key: item.key,
                            value: item.categoryValue.raw,
                          },
                        ],
                      });
                    }}
                  />
                  {series.showValues && (
                    <text
                      className={styles.valueLabel}
                      x={seriesValueScale(value)}
                      y={y}
                      dx={7}
                      dy={1 + groupSeriesScale.bandwidth() / 2}
                      fill={increaseBrightness(colorScale(key), 20)}>
                      {formatPropertyValue(value, {
                        type: series.type,
                        format: series.format,
                        style: 'compact',
                      })}
                    </text>
                  )}
                </Fragment>
              );
            });
          })}
          <AxisLeft
            scale={categoryScale}
            stroke={gridLineColor}
            hideTicks
            tickClassName={styles.axisLabel}
            tickLabelProps={{
              fill: '#FFF',
              fontSize: 12,
              textAnchor: 'end',
              width: 100,
              scaleToFit: 'shrink-only',
            }}
            tickComponent={({ formattedValue, ...tickProps }) => {
              const dataItem = data.items.find(
                (item) => item.categoryValue.formatted === formattedValue,
              );
              return dataItem !== undefined ? (
                <text
                  {...InteractableProps}
                  {...tickProps}
                  dx={0}
                  onClick={(e) =>
                    onCategoryClick?.({
                      x: e.clientX,
                      y: e.clientY,
                      key: dataItem.key,
                      groupFilters: [{ key: dataItem.key, value: dataItem.categoryValue.raw }],
                    })
                  }>
                  <title>{formattedValue}</title>
                  {truncate(formattedValue, { length: CategoryLabelMaxLength })}
                </text>
              ) : null;
            }}
            tickValues={categoryScale.domain()} // Always render all tick values
          />
          {domainSizes.left > 0 && (
            <Axis
              left={5}
              top={height - 25}
              hideTicks
              scale={valueScales.left}
              tickClassName={commonStyles.tickLabel}
              tickLabelProps={() => ({ textAnchor: 'start' })}
              tickFormat={(n) => formatBottomAxisValue(n.valueOf())}
              tickValues={valueScales.left.ticks(NumXTicks)}
              hideAxisLine
            />
          )}
          {domainSizes.right > 0 && hasRightAxisSeries && (
            <Axis
              left={6}
              top={-4}
              hideTicks
              scale={valueScales.right}
              tickClassName={commonStyles.tickLabel}
              tickLabelProps={() => ({ textAnchor: 'start' })}
              tickFormat={(n) => formatTopAxisValue(n.valueOf())}
              tickValues={topAxisTicks}
              hideAxisLine
            />
          )}
        </Group>
      </svg>
    </div>
  );
};
